import Application from '@Common/ApplicationCore/Application';
import constants from '@Common/Core/Constant';
import Guid from '@Common/Core/Guid';
import instanceFactory, {
    AbstractInstanceItem,
    ApplicationSettings,
    Categorized,
    DatabaseServer,
    DocumentAttribute,
    FontFamily,
    Format,
    RFunction,
    RModel,
    Tenant
} from '@InstanceFactory';
import { BaseEditor } from '@Studio/Editors/BaseEditor';

import documentUtils from '../Helpers/DocumentUtils';
import {LoginService} from 'InstanceFactory';

namespace documentsFactory {
    type EditorConstructor = GenericConstructor<BaseEditor<any>>;

    export interface IDocumentDescriptor {
        readonly documentType?: string;
        readonly contextHelpId?: string;
        readonly category?: string;
        readonly displayName?: string;
        readonly icon?: string;
        readonly getImage?: (descriptor) => string;
        readonly getDisplayName?: (descriptor) => string;
        readonly storedAttributes?: string[];
        /**@deprecated*/
        readonly editorType?: EditorConstructor;
        readonly editorTypeMappings?: { attributes: Dictionary<string>; editorType: EditorConstructor }[];
    }

    const descriptors = initDescriptors();
    const descByCategory = _(descriptors)
        .filter(desc => !!desc.category)
        .mapKeys(desc => desc.category)
        .value();
    const descByDocumentType = _(descriptors)
        .filter(desc => !!desc.documentType)
        .mapKeys(desc => desc.documentType)
        .value();
    const typeByCategory = _.invertBy(instanceFactory, (value: any | Categorized) => value.category);

    export function registerDocumentDescriptor(desc: IDocumentDescriptor): void {
        if (desc.category) {
            descByCategory[desc.category] = desc;
        }
        if (desc.documentType) {
            descByDocumentType[desc.documentType] = desc;
        }
    }

    export function findDocumentDescriptorByCategory(category: string, throwable = true): IDocumentDescriptor {
        const descriptor = descByCategory[category];
        if (!descriptor && throwable) {
            throw new Error(`Unknown document category: ${category}`);
        }
        return descriptor;
    }

    export function findDocumentDescriptor(document: string | { category?: string; $type?: string }, throwable: boolean = true): IDocumentDescriptor {
        const descriptor = Array.from(findDocumentDescriptorInner(document)).find(d => !!d);
        if (!descriptor && throwable) {
            throw new Error(`Unknown document type: ${JSON.stringify(document)}`);
        }
        return descriptor;
    }

    function* findDocumentDescriptorInner(document: string | { category?: string; $type?: string }): IterableIterator<IDocumentDescriptor> {
        if (_.isString(document)) {
            yield descByDocumentType[document];
        } else {
            if (document.category) {
                yield descByCategory[document.category] || descByDocumentType[document.category];
            }
            yield descByDocumentType[documentUtils.getReferencedClassName(document)];
        }
    }

    export function getDocumentType(category): string {
        const descriptor = findDocumentDescriptor(category, false) || findDocumentDescriptorByCategory(category, false) || { documentType: category };
        return descriptor.documentType;
    }

    export function getEditor(documentType: string, desc?: { attributes?: DocumentAttribute[] }): BaseEditor<any> {
        const documentDescriptor = findDocumentDescriptor(documentType);
        const mapping = documentDescriptor.editorTypeMappings || [];
        let { attributes } = desc || { attributes: [] };
        attributes = attributes || [];
        const descAttrs = attributes.reduce((p, c) => ((p[c.name] = c.value), p), {});
        let matched = _.find(mapping, m => _.isMatch(descAttrs, m.attributes));
        if (!matched) {
            matched = _.find(mapping, m => _.isMatch({ [defaultEditorAttrName]: '' }, m.attributes));
        }
        return new matched.editorType({ application: Application.current });
    }

    export function getDocumentContexHelpId(category: string): string {
        const descriptor = findDocumentDescriptor(category, false) || findDocumentDescriptorByCategory(category, false) || { contextHelpId: null };
        return descriptor.contextHelpId;
    }

    export function getIconByDocumentType(documentType: string | { category?: string; $type?: string }, throwable = true): string {
        const documentDescriptor = findDocumentDescriptor(documentType, throwable) || {};
        return documentDescriptor.icon;
    }

    export function getCategoryDisplayName(category: string): string {
        return findDocumentDescriptorByCategory(category).displayName;
    }

    const emptyDesctriptor: any = {};

    export function getIconByDesctriptor(descriptor): string {
        const desc = findDocumentDescriptor(descriptor, false) || emptyDesctriptor;

        if (desc.getImage) {
            return desc.getImage(descriptor);
        }
        return desc.icon;
    }

    export function getDisplayNameByDesctriptor(descriptor): string {
        const desc = findDocumentDescriptor(descriptor, false) || emptyDesctriptor;
        if (desc.getDisplayName) {
            return desc.getDisplayName(descriptor);
        }
        return descriptor.name;
    }

    export function getTypeByCategory(category: string): KeyOfByType<typeof instanceFactory, AbstractInstanceItem<any>> {
        return ((_.first(typeByCategory[category]) || category) as unknown) as any;
    }

    function initDescriptors(): IDocumentDescriptor[] {
        return [
            {
                documentType: constants.studioDocumentTypes.view,
                contextHelpId: constants.contextHelpIds.studio.dashboardDesigner,
                category: constants.documentCategories.view,
                displayName: strings.documentFactory.viewComponent,
                icon: 'View.png'
            },
            {
                documentType: constants.studioDocumentTypes.record,
                contextHelpId: constants.contextHelpIds.studio.record,
                category: constants.documentCategories.record,
                displayName: strings.documentFactory.recordDocument,
                storedAttributes: ['RecordType', 'RecordVirtual'],
                icon: 'Documents/record.png',
                getImage: function (descriptor) {
                    if (!descriptor.attributes) {
                        return this.icon;
                    }

                    let imagePath = 'Documents/';

                    if (documentUtils.isRecordOnRecordDocument(descriptor)) {
                        imagePath += 'derived record';
                    } else {
                        imagePath += 'direct source records';
                    }

                    if (documentUtils.isOnDemandDocument(descriptor)) {
                        imagePath += '_virtual';
                    }

                    imagePath += '.png';

                    return imagePath;
                }
            },
            {
                documentType: constants.studioDocumentTypes.rModel,
                contextHelpId: constants.contextHelpIds.studio.rModel,
                category: RModel.category,
                displayName: strings.documentFactory.rModel,
                icon: 'Documents/rmodel.png'
            },
            {
                documentType: constants.studioDocumentTypes.rFunction,
                contextHelpId: constants.contextHelpIds.studio.rFunction,
                category: RFunction.category,
                displayName: strings.documentFactory.rFunction,
                icon: 'Documents/rfunction.png'
            },
            {
                documentType: constants.studioDocumentTypes.translationTable,
                contextHelpId: constants.contextHelpIds.studio.translationTable,
                category: constants.documentCategories.tt,
                displayName: strings.documentFactory.translationTable,
                icon: 'Documents/translation table.png'
            },
            {
                documentType: constants.studioDocumentTypes.dataSource,
                contextHelpId: constants.contextHelpIds.studio.dataSource,
                category: constants.documentCategories.dataSource,
                displayName: strings.documentFactory.dataSource,
                icon: 'Documents/data sources.png'
            },
            {
                documentType: constants.studioDocumentTypes.report,
                category: constants.documentCategories.report,
                displayName: strings.documentFactory.reportSchedule,
                icon: 'Documents/reports.png'
            },
            {
                documentType: constants.studioDocumentTypes.executionPlan,
                contextHelpId: constants.contextHelpIds.studio.executionPlan,
                category: constants.documentCategories.executionPlan,
                displayName: strings.documentFactory.executionPlan,
                icon: 'Documents/execution plan.png'
            },
            {
                documentType: constants.studioDocumentTypes.metricPool,
                contextHelpId: constants.contextHelpIds.studio.metricPool,
                category: constants.documentCategories.metricPool,
                displayName: strings.documentFactory.metricPool,
                storedAttributes: ['RecordType', 'RecordVirtual'],
                icon: 'Documents/metric-pool.png',
                getImage: function (descriptor) {
                    if (!descriptor.attributes) {
                        return this.icon;
                    }

                    const imageInfo = {
                        isRecordOnRecord: documentUtils.isRecordOnRecordDocument(descriptor),
                        isOnDemand: documentUtils.isOnDemandDocument(descriptor)
                    };
                    return `Documents/Metric-Pool-${imageInfo.isOnDemand ? 'OnDemand' : 'stored'}-${
                        imageInfo.isRecordOnRecord ? 'onRecord' : 'onSource'
                    }.png`;
                }
            },
            {
                documentType: constants.studioDocumentTypes.metric,
                category: constants.documentCategories.metric,
                displayName: strings.documentFactory.metric,
                icon: 'Documents/metric.png'
            },
            {
                category: constants.documentCategories.metricStandard,
                displayName: strings.documentFactory.strandardMetric
            },
            {
                category: constants.documentCategories.olapMetric,
                displayName: strings.documentFactory.mdxMetric
            },
            {
                documentType: constants.studioDocumentTypes.processMetric,
                contextHelpId: constants.contextHelpIds.studio.processMetric,
                category: constants.documentCategories.metricProcess,
                displayName: strings.documentFactory.processMetric,
                icon: 'StudioTree/32x32/dark/Image-process.png'
            },
            {
                documentType: constants.studioDocumentTypes.derivedMetric,
                contextHelpId: constants.contextHelpIds.studio.derivedMetric,
                category: constants.documentCategories.metricDerived,
                displayName: strings.documentFactory.derivedMetric,
                icon: 'Documents/derived metric.png'
            },
            {
                documentType: constants.studioDocumentTypes.metricGroup,
                contextHelpId: constants.contextHelpIds.studio.metricGroup,
                category: constants.documentCategories.metricGroup,
                displayName: strings.documentFactory.metricGroupDocument,
                icon: 'Documents/metric group.png'
            },
            {
                documentType: constants.studioDocumentTypes.regressionMetric,
                contextHelpId: constants.contextHelpIds.studio.regressionMetric,
                category: constants.documentCategories.metricRegression,
                displayName: strings.documentFactory.regressionMetric,
                icon: 'Documents/forecast metric.png'
            },
            {
                documentType: constants.adminDocumentTypes.projectDocument,
                contextHelpId: constants.contextHelpIds.admin.project,
                displayName: strings.documentFactory.projectDefinition,
                icon: 'StudioTree/dark/project.png'
            },
            {
                documentType: constants.adminDocumentTypes.sharedConnectionDocument,
                contextHelpId: constants.contextHelpIds.admin.connection,
                displayName: strings.documentFactory.connection,
                icon: 'Documents/data sources.png'
            },
            {
                documentType: constants.adminDocumentTypes.filterDocument,
                contextHelpId: constants.contextHelpIds.admin.userFilter,
                displayName: strings.documentFactory.filter,
                icon: 'StudioTree/dark/filter.png'
            },
            {
                documentType: constants.adminDocumentTypes.externalProgram,
                contextHelpId: constants.contextHelpIds.admin.externalProgram,
                displayName: strings.documentFactory.externalProgram,
                icon: 'Documents/run.png'
            },
            {
                documentType: constants.studioDocumentTypes.process,
                contextHelpId: constants.contextHelpIds.studio.process,
                category: constants.documentCategories.process,
                displayName: strings.documentFactory.process,
                storedAttributes: ['IsAutogenerated'],
                icon: 'Documents/process.png'
            },
            {
                documentType: constants.studioDocumentTypes.dataLoadModule,
                contextHelpId: constants.contextHelpIds.studio.dataload,
                displayName: strings.documentFactory.dataLoadModule,
                icon: 'StudioTree/dark/data load.png'
            },
            {
                documentType: constants.adminDocumentTypes.userDocument,
                contextHelpId: constants.contextHelpIds.admin.insightUser,
                displayName: strings.documentFactory.insightUser,
                icon: 'StudioTree/light/user.png',
                getDisplayName: function (descriptor) {
                    return descriptor.login || descriptor.name;
                }
            },
            {
                documentType: constants.adminDocumentTypes.roleDocument,
                contextHelpId: constants.contextHelpIds.admin.role,
                displayName: strings.documentFactory.role,
                icon: 'StudioTree/dark/role.png'
            },
            {
                documentType: constants.adminDocumentTypes.calendar,
                contextHelpId: constants.contextHelpIds.admin.calendar,
                displayName: strings.documentFactory.calendarObject,
                icon: 'StudioTree/dark/calendar.png'
            },
            {
                documentType: constants.adminDocumentTypes.financialYearDocument,
                contextHelpId: constants.contextHelpIds.admin.financialYear,
                displayName: strings.documentFactory.financialYear,
                icon: 'StudioTree/dark/financialYear.svg'
            },
            //#region DataLaod
            {
                documentType: constants.dataloadDocumentTypes.executionPlansEditor,
                contextHelpId: constants.contextHelpIds.dataLoader.executionPlans,
                displayName: strings.documentFactory.executionPlansEditor,
                icon: 'Documents/execution plan.png'
            },
            {
                documentType: constants.dataloadDocumentTypes.loadConsoleEditor,
                contextHelpId: constants.contextHelpIds.dataLoader.loadConsole,
                displayName: strings.documentFactory.loadConsoleEditor,
                icon: 'console.png'
            },
            {
                documentType: constants.dataloadDocumentTypes.schedulerLogEditor,
                contextHelpId: constants.contextHelpIds.dataLoader.schedulerLogs,
                displayName: strings.documentFactory.schedulerLogEditor,
                icon: 'scheduler/scheduler-log.png'
            },
            {
                documentType: constants.dataloadDocumentTypes.recordDates,
                contextHelpId: constants.contextHelpIds.dataLoader.recordDates,
                displayName: strings.documentFactory.recordDates,
                icon: 'record-dates.png'
            },
            {
                documentType: constants.dataloadDocumentTypes.statisticsEditor,
                displayName: strings.documentFactory.statisticsDashboard,
                icon: 'StudioTree/dark/project.png'
            },
            //#endregion DataLoad
            {
                documentType: ApplicationSettings.category,
                contextHelpId: constants.contextHelpIds.admin.authentication,
                displayName: strings.documentFactory.applicationSettings,
                icon: 'StudioTree/dark/authentication.png'
            },
            {
                documentType: constants.studioDocumentTypes.guide,
                contextHelpId: constants.contextHelpIds.studio.guide,
                displayName: strings.documentFactory.guide,
                icon: 'StudioTree/light/guide.png'
            },
            {
                documentType: constants.adminDocumentTypes.userMappingTab,
                contextHelpId: constants.contextHelpIds.admin.userMapping,
                displayName: strings.documentFactory.mappingRules,
                icon: 'StudioTree/dark/User mapping.png'
            },
            {
                documentType: constants.adminDocumentTypes.logsTab,
                contextHelpId: constants.contextHelpIds.admin.logs,
                displayName: strings.documentFactory.logSettingsInfo,
                icon: 'StudioTree/dark/logs.png'
            },
            {
                documentType: constants.adminDocumentTypes.adminDashboard,
                contextHelpId: constants.contextHelpIds.admin.dashboard,
                displayName: strings.adminDashboard.tabName,
                icon: 'StudioTree/light/guide.png'
            },
            {
                documentType: constants.studioDocumentTypes.fileProviderTemplate,
                contextHelpId: constants.contextHelpIds.studio.fileProviderTemplate,
                category: constants.documentCategories.fileProviderTemplate,
                displayName: strings.documentFactory.parsingTemplateDS,
                icon: 'Documents/fp-templates.png'
            },
            {
                documentType: constants.studioDocumentTypes.fileProviderBatch,
                contextHelpId: constants.contextHelpIds.studio.fileProviderBatch,
                category: constants.documentCategories.fileProviderBatch,
                displayName: strings.documentFactory.batchDS,
                icon: 'Documents/fp-batch.png'
            },
            {
                documentType: constants.studioDocumentTypes.fileProviderServer,
                contextHelpId: constants.contextHelpIds.studio.fileProviderServer,
                category: constants.documentCategories.fileProviderDownloaderServer,
                displayName: strings.documentFactory.serverDS,
                icon: 'Documents/fp-ftp server.png'
            },
            {
                documentType: constants.studioDocumentTypes.fileProviderTask,
                contextHelpId: constants.contextHelpIds.studio.fileProviderTask,
                category: constants.documentCategories.fileProviderDownloaderTask,
                displayName: strings.documentFactory.taskDS,
                icon: 'Documents/fp-ftp task.png'
            },
            {
                documentType: constants.studioDocumentTypes.fileProviderJobLog,
                contextHelpId: constants.contextHelpIds.studio.fileProviderJobLog,
                displayName: strings.documentFactory.jobsLog,
                icon: 'Documents/fp-job.png'
            },
            {
                documentType: constants.studioDocumentTypes.fileProviderManJob,
                contextHelpId: constants.contextHelpIds.studio.runManualJob,
                displayName: strings.documentFactory.manualJob,
                icon: 'Documents/fp-run job.png'
            },
            {
                documentType: constants.studioDocumentTypes.fileProviderFTPLog,
                contextHelpId: constants.contextHelpIds.studio.fileProviderFTPLog,
                displayName: strings.documentFactory.ftpLog,
                icon: 'Documents/fp-ftp log.png'
            },
            {
                documentType: constants.adminDocumentTypes.optionsTab,
                contextHelpId: constants.contextHelpIds.admin.options,
                displayName: strings.documentFactory.optionsTab,
                icon: 'StudioTree/light/options.png'
            },
            {
                documentType: constants.adminDocumentTypes.distributionTab,
                contextHelpId: constants.contextHelpIds.admin.distribution,
                displayName: strings.documentFactory.distributionOptions,
                icon: 'StudioTree/dark/distribution.png'
            },
            {
                documentType: constants.documentCategories.parameter,
                contextHelpId: constants.contextHelpIds.studio.parameter,
                category: constants.documentCategories.parameter,
                displayName: strings.documentFactory.parameter,
                icon: 'Documents/parameter.png'
            },
            {
                documentType: constants.documentCategories.constantDocument,
                contextHelpId: constants.contextHelpIds.studio.constant,
                category: constants.documentCategories.constant,
                displayName: strings.documentFactory.constantDocument,
                icon: 'Documents/constant.png'
            },
            {
                documentType: constants.themeManagerDocumentTypes.themeDocument,
                contextHelpId: constants.contextHelpIds.themeManager.theme,
                displayName: strings.documentFactory.dashboardTheme,
                icon: 'StudioTree/light/themes.png'
            },
            {
                documentType: constants.adminDocumentTypes.licenseManagerTab,
                contextHelpId: constants.contextHelpIds.admin.license,
                displayName: strings.documentFactory.licenseManagerTab,
                icon: 'StudioTree/dark/license-manager.png'
            },
            {
                documentType: constants.adminDocumentTypes.licenseSettings,
                displayName: strings.documentFactory.licensSettings,
                icon: 'StudioTree/dark/license-manager.png'
            },
            {
                documentType: constants.themeManagerDocumentTypes.dateFormatDocument,
                contextHelpId: constants.contextHelpIds.themeManager.dateFormat,
                displayName: strings.documentFactory.dateTimeFormat,
                icon: 'StudioTree/light/date-formats.png'
            },
            {
                documentType: constants.documentCategories.dataCacheAccelerator,
                contextHelpId: constants.contextHelpIds.studio.accelerator,
                category: constants.documentCategories.accelerator,
                displayName: strings.documentFactory.dataCacheAccelerator,
                icon: 'Documents/accelerator.png'
            },
            {
                documentType: constants.themeManagerDocumentTypes.formatDocument,
                contextHelpId: constants.contextHelpIds.themeManager.format,
                category: Format.category,
                displayName: strings.documentFactory.format,
                icon: 'StudioTree/light/formats.png'
            },
            {
                documentType: constants.themeManagerDocumentTypes.tooltipDocument,
                displayName: strings.documentFactory.tooltipFormat,
                getImage: function (doc) {
                    if (doc && doc.isLabelFormat) {
                        return 'StudioTree/light/label-formats.png';
                    }
                    return 'StudioTree/light/tooltip-formats.png';
                }
            },
            {
                documentType: FontFamily.instanceName,
                contextHelpId: constants.contextHelpIds.themeManager.fontFamilies,
                displayName: strings.fontFamily.sectionName,
                icon: 'Commands/font.png'
            },
            {
                documentType: constants.adminDocumentTypes.filterGroupDocument,
                contextHelpId: constants.contextHelpIds.admin.filterGroup,
                displayName: strings.documentFactory.filterGroupDocument,
                icon: 'StudioTree/light/filter-groups.png'
            },
            {
                documentType: constants.themeManagerDocumentTypes.viewLayout,
                contextHelpId: constants.contextHelpIds.themeManager.viewLayout,
                displayName: strings.viewLayout.section,
                icon: 'StudioTree/dark/View-Layout.png'
            },
            {
                documentType: constants.alertMessageTypes.groupAlertMessage,
                contextHelpId: constants.contextHelpIds.admin.alert,
                displayName: strings.documentFactory.groupAlertDocument,
                icon: 'StudioTree/dark/alerts.png'
            },
            {
                documentType: constants.studioDocumentTypes.audit,
                contextHelpId: constants.contextHelpIds.studio.audit,
                displayName: strings.documentFactory.audit,
                icon: 'StudioTree/light/audit.png'
            },
            {
                documentType: constants.studioDocumentTypes.testCase,
                category: constants.documentCategories.testCase,
                contextHelpId: constants.contextHelpIds.studio.testProject,
                displayName: strings.documentFactory.testProject,
                icon: 'StudioTree/dark/Test-case_dark.png'
            },
            {
                documentType: constants.studioDocumentTypes.testSession,
                contextHelpId: constants.contextHelpIds.studio.testSession,
                displayName: strings.documentFactory.testSession,
                icon: 'StudioTree/dark/Test-run_dark.png'
            },

            {
                documentType: constants.studioDocumentTypes.olapDocument,
                contextHelpId: constants.contextHelpIds.studio.olapDocument,
                category: constants.documentCategories.olapDocument,
                displayName: strings.documentFactory.olapDocument,
                icon: 'Documents/olap metric.png'
            },
            {
                category: constants.documentCategories.autoMetric,
                displayName: strings.documentFactory.autoMetric
            },
            {
                category: constants.documentCategories.alert,
                displayName: strings.documentFactory.alert
            },
            {
                category: constants.documentCategories.regression,
                displayName: strings.documentFactory.regression
            },
            {
                category: constants.documentCategories.recordAlert,
                displayName: strings.documentFactory.recordAlert
            },
            {
                category: constants.documentCategories.processAlert,
                displayName: strings.documentFactory.processAlert
            },
            {
                category: constants.documentCategories.metricAlert,
                displayName: strings.documentFactory.metricAlert
            },
            {
                category: constants.documentCategories.customFilterGroup,
                displayName: strings.documentFactory.customFilterGroup
            },
            {
                category: constants.documentCategories.wsDataSource,
                displayName: strings.documentFactory.wsDataSource
            },
            {
                category: constants.documentCategories.dataSourceTable,
                displayName: strings.documentFactory.dataSourceTable
            },
            {
                category: constants.documentCategories.dataSourceWSMethod,
                displayName: strings.documentFactory.dataSourceWsMethod
            },
            {
                documentType: constants.documentCategories.folder,
                category: constants.documentCategories.folder,
                displayName: strings.documentFactory.folder
            },
            {
                category: constants.documentCategories.titleColorList,
                displayName: strings.documentFactory.colorsList
            },
            {
                category: constants.documentCategories.dimension,
                displayName: strings.documentFactory.dimension
            },
            {
                category: constants.documentCategories.dataCacheAccelerator,
                displayName: strings.documentFactory.dataCacheAccelerator
            },
            {
                category: constants.documentCategories.solutionConfigDocument,
                displayName: strings.documentFactory.configuration
            },
            {
                documentType: constants.adminDocumentTypes.sqlFunctionDocument,
                contextHelpId: constants.contextHelpIds.admin.sqlFunction,
                displayName: strings.documentFactory.sqlFunctionSection,
                icon: 'StudioTree/dark/project.png'
            },
            {
                documentType: constants.studioDocumentTypes.geoData,
                contextHelpId: constants.contextHelpIds.studio.geoMap,
                category: constants.documentCategories.geoDatabase,
                displayName: strings.documentFactory.geoData,
                icon: 'StudioTree/light/geo-data.png'
            },
            {
                documentType: constants.studioDocumentTypes.webService,
                contextHelpId: constants.contextHelpIds.studio.webService,
                category: constants.documentCategories.webService,
                displayName: strings.documentFactory.webService,
                icon: 'StudioTree/light/Remote-Service.png'
            },
            {
                documentType: DatabaseServer.instanceName,
                category: DatabaseServer.category,
                displayName: strings.propertyGridTypes[DatabaseServer.typeName].displayName,
                contextHelpId: constants.contextHelpIds.mtconsole.databaseServer,
                icon: 'Documents/databaseServer.png'
            },
            {
                documentType: Tenant.instanceName,
                category: Tenant.category,
                displayName: strings.propertyGridTypes[Tenant.typeName].displayName,
                icon: 'Documents/tenant.png',
                contextHelpId: constants.contextHelpIds.mtconsole.tenant
            },
            {
                documentType: LoginService.instanceName,
                category: LoginService.category,
                displayName: strings.propertyGridTypes[LoginService.typeName].displayName,
                icon: 'StudioTree/light/Remote-Service.png',
            },
            {
                category: constants.documentCategories.processFilterDefinition
            },
            {
                category: constants.documentCategories.swimlaneGroup
            }
        ];
    }

    const defaultEditorAttrName = Guid.newGuid();

    export function registerEditor(typeName: string, attributes: Dictionary<string>, editorType: { new (...args): BaseEditor<any> }): void {
        const desc = descByDocumentType[typeName];
        if (desc) {
            desc['editorTypeMappings' as string] = desc.editorTypeMappings || [];
            desc.editorTypeMappings.push({ attributes: attributes || { [defaultEditorAttrName]: '' }, editorType });
        } else {
            console.error(typeName, editorType);
        }
    }

    export function filterAttributes(attributes: DocumentAttribute[]): DocumentAttribute[] {
        if (!attributes) {
            return attributes;
        }
        const allAttrs = descriptors.reduce(
            (prev, { storedAttributes = [], editorTypeMappings = [] }) => [
                ...prev,
                ...storedAttributes,
                ...editorTypeMappings.flatMap(m => Object.keys(m.attributes))
            ],
            []
        );
        const set = new Set(allAttrs);
        return attributes.filter(a => set.has(a.name));
    }
}

export default documentsFactory;
